import dayjs, { type Dayjs } from 'dayjs'

export type PeriodRange = [Dayjs, Dayjs]

export interface PeriodPreset {
    key: string
    label: string
    range: (now?: Dayjs) => PeriodRange
}

/** Початок кварталу: перший місяць кварталу (0, 3, 6, 9) */
const quarterStart = (date: Dayjs): Dayjs => date.month(Math.floor(date.month() / 3) * 3).startOf('month')

/** Початок тижня — понеділок (як date_trunc('week') на бекенді), незалежно від локалі dayjs */
export const weekStart = (date: Dayjs): Dayjs => date.subtract((date.day() + 6) % 7, 'day').startOf('day')

/** Швидкі періоди звіту */
export const PERIOD_PRESETS: PeriodPreset[] = [
    { key: 'today', label: 'Сьогодні', range: (now = dayjs()) => [now.startOf('day'), now.endOf('day')] },
    {
        key: 'week',
        label: 'Тиждень',
        range: (now = dayjs()) => [weekStart(now), weekStart(now).add(6, 'day').endOf('day')],
    },
    { key: 'month', label: 'Місяць', range: (now = dayjs()) => [now.startOf('month'), now.endOf('month')] },
    {
        key: 'prevMonth',
        label: 'Минулий місяць',
        range: (now = dayjs()) => {
            const prev = now.subtract(1, 'month')
            return [prev.startOf('month'), prev.endOf('month')]
        },
    },
    {
        key: 'quarter',
        label: 'Квартал',
        range: (now = dayjs()) => [quarterStart(now), quarterStart(now).add(2, 'month').endOf('month')],
    },
    { key: 'year', label: 'Рік', range: (now = dayjs()) => [now.startOf('year'), now.endOf('year')] },
]

/** Період за замовчуванням — поточний місяць */
export const defaultPeriod = (now: Dayjs = dayjs()): PeriodRange => [now.startOf('month'), now.endOf('month')]

/** Ключ швидкого періоду, що точно збігається з вибраним (для підсвітки), або null */
export const matchPreset = (period: PeriodRange | null, now: Dayjs = dayjs()): string | null => {
    if (!period) return null
    const preset = PERIOD_PRESETS.find((item) => {
        const [from, to] = item.range(now)
        return from.isSame(period[0], 'day') && to.isSame(period[1], 'day')
    })
    return preset?.key ?? null
}

const MONTHS = [
    'Січень',
    'Лютий',
    'Березень',
    'Квітень',
    'Травень',
    'Червень',
    'Липень',
    'Серпень',
    'Вересень',
    'Жовтень',
    'Листопад',
    'Грудень',
]

/**
 * Підпис періоду-групи з ключа API (`YYYY-MM-DD` — початок дня / тижня / місяця):
 * день — «30.09.2026», тиждень — «28.09 – 04.10.2026», місяць — «Вересень 2026».
 */
export const formatPeriodKey = (unit: 'day' | 'week' | 'month', key: string): string => {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(key) ? dayjs(key) : null
    if (!date || !date.isValid()) return key
    if (unit === 'month') return `${MONTHS[date.month()]} ${date.year()}`
    if (unit === 'week') {
        const end = date.add(6, 'day')
        const startFormat = date.year() === end.year() ? 'DD.MM' : 'DD.MM.YYYY'
        return `${date.format(startFormat)} – ${end.format('DD.MM.YYYY')}`
    }
    return date.format('DD.MM.YYYY')
}

/** Підпис періоду для шапки: «01.09 – 30.09.2026», «30.09.2026» або «за весь час» */
export const formatPeriodLabel = (period: PeriodRange | null): string => {
    if (!period) return 'за весь час'
    const [from, to] = period
    if (from.isSame(to, 'day')) return from.format('DD.MM.YYYY')
    const fromFormat = from.year() === to.year() ? 'DD.MM' : 'DD.MM.YYYY'
    return `${from.format(fromFormat)} – ${to.format('DD.MM.YYYY')}`
}
